import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface UsersdashActivityDailyDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** Daily DAU loader. Outputs total `dau` + `new_users`, plus per-META-user-type DAU
 *  (`all_dau`, `meta_dau`, `hopa_dau`, `core_loop_dau`, `meta_productive_dau`) derived from the
 *  player_days user-type flags surfaced on dashboard_users_activity (UBI-1139). Used by the
 *  mopa-users DAU chart and the META Summary / Engagement DAU charts. */
export class UsersdashActivityDailyDataLoaderFactory extends Factory<
    DataLoader,
    UsersdashActivityDailyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'UsersdashActivityDailyDataLoaderFactory';

    getInstance(options: UsersdashActivityDailyDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                // const maxDayNo =
                //     queryContext.settings[
                //         QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                //     ] ?? 30;
                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: [`s_day_no <= ${maxDayNo}`],
                // };

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT
  ${splitSelect}date,
  COUNT(DISTINCT player_id) AS dau,
  COUNT(DISTINCT d0_player_id) AS new_users,
  -- per-META-user-type DAU from the player_days user-type flags (UBI-1139)
  COUNT(DISTINCT player_id) AS all_dau,
  COUNT(DISTINCT IF(meta_user, player_id, NULL)) AS meta_dau,
  COUNT(DISTINCT IF(hopa_user, player_id, NULL)) AS hopa_dau,
  COUNT(DISTINCT IF(core_loop_user, player_id, NULL)) AS core_loop_dau,
  COUNT(DISTINCT IF(meta_prod_user, player_id, NULL)) AS meta_productive_dau,
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      datasourceSqlMap.dataset,
      datasourceSqlMap.tableId
  )}\` ds
${join}
${where}
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date ASC`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
